// ─── Coquille d'application (Core) ────────────────────────────────
// Structure commune à TOUTE app de la suite, levée de MoPCal :
//   [ contenu de l'onglet ]
//   [ bandeau de pub      ]   (optionnel)
//   [ pied de version     ]
//   [ barre d'onglets     ]
// L'app hôte ne fournit que ses onglets. Non connecté → écran de connexion.
//
//   <CoquilleApp
//     onglets={[{ cle:'Accueil', label:'Accueil', icone:'👋', composant: MonEcran }]}
//     badges={{ Accueil: 0 }}
//     avecPub
//   />
import React, { useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Keyboard, Modal } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUI } from '../theme/PreferencesUI';
import { useAuth } from '../auth/AuthContext';
import { useEspace } from '../espace/EspaceContext';
import EcranConnexion from '../auth/EcranConnexion';
import VersionFooter from './VersionFooter';
import PubZone from '../pub/PubZone';
import { MenuOngletProvider, useMenuOnglet } from './MenuOngletContext';
import { useT } from '../i18n/LangueContext';

const Tab = createBottomTabNavigator();

// Écran de chargement plein écran (spinner + message). Bloque l'usage tant que
// les données de base ne sont pas prêtes — principe app-wide.
function EcranChargement({ UI, message }) {
  return (
    <View style={{ flex: 1, backgroundColor: UI.bg, alignItems: 'center', justifyContent: 'center', gap: 14 }}>
      <ActivityIndicator size="large" color={UI.accent} />
      {!!message && <Text style={{ color: UI.muted, fontSize: 13.5, fontWeight: '600' }}>{message}</Text>}
    </View>
  );
}

// Barre d'onglets maison (comme MoPCal : la barre native n'est pas utilisée),
// qui empile pub + pied de version au-dessus des onglets.
function BarreOnglets({ state, navigation, onglets, badges, avecPub }) {
  const UI = useUI();
  const insets = useSafeAreaInsets();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const parCle = useMemo(() => Object.fromEntries(onglets.map(o => [o.cle, o])), [onglets]);
  const { menus, doubleTaps, signalReset } = useMenuOnglet();
  const [largeur, setLargeur] = useState(0);          // largeur mesurée de la barre
  const [menu, setMenu] = useState(null);             // { cle, index } — menu déroulant ouvert
  const dernierTap = useRef({ key: null, t: 0 });     // détection double-clic d'onglet

  // Onglets VISIBLES dans la barre (les onglets `masque` restent navigables mais
  // ne s'affichent pas : accès via un menu d'appui long, ex. IVV réservé).
  const visibles = state.routes
    .map((route, index) => ({ route, index, o: parCle[route.name] }))
    .filter(x => x.o && !x.o.masque);
  const nb = visibles.length || 1;
  const colW = largeur ? largeur / nb : 0;
  const items = menu ? (menus[menu.cle] || []) : [];

  return (
    <View>
      {avecPub && <PubZone />}
      <VersionFooter />
      <View onLayout={(e) => setLargeur(e.nativeEvent.layout.width)}
        style={[s.bar, { paddingBottom: insets.bottom + 4, height: 56 + insets.bottom }]}>
        {visibles.map(({ route, index, o }, vi) => {
          const focus = state.index === index;
          const couleur = focus ? UI.accent : UI.faint;
          const n = (badges && badges[route.name]) || 0;
          const aMenu = (menus[route.name] || []).length > 0;
          return (
            <TouchableOpacity
              key={route.key}
              style={s.item}
              accessibilityRole="button"
              accessibilityState={focus ? { selected: true } : {}}
              delayLongPress={350}
              onLongPress={aMenu ? () => { Keyboard.dismiss(); setMenu({ cle: route.name, vi }); } : undefined}
              onPress={() => {
                Keyboard.dismiss();
                // Double-clic sur l'onglet (ex. sélecteur de calendriers) : 2 taps < 320 ms.
                const dt = doubleTaps[route.name];
                const now = Date.now();
                if (dt && dernierTap.current.key === route.key && (now - dernierTap.current.t) < 320) {
                  dernierTap.current = { key: null, t: 0 };
                  dt();
                  return;
                }
                dernierTap.current = { key: route.key, t: now };
                // Clic sur un onglet → l'écran cible ferme ses modales et revient à sa page.
                signalReset(route.name);
                const evt = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });
                if (!focus && !evt.defaultPrevented) navigation.navigate(route.name);
              }}
            >
              <View style={{ position: 'relative' }}>
                <Text style={s.icon}>{o.icone}</Text>
                {aMenu && <Text style={s.chevMenu}>⌄</Text>}
                {n > 0 && (
                  <View style={s.dot}><Text style={s.dotTxt}>{n > 99 ? '99+' : n}</Text></View>
                )}
              </View>
              <Text style={[s.label, { color: couleur }]} numberOfLines={1}>{o.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Menu déroulant d'onglet (appui long) — ancré au-dessus de l'onglet. */}
      <Modal visible={!!menu} transparent animationType="fade" onRequestClose={() => setMenu(null)}>
        <TouchableOpacity style={s.menuFond} activeOpacity={1} onPress={() => setMenu(null)}>
          <View style={[
            s.menu,
            { bottom: insets.bottom + 62,
              left: Math.max(8, Math.min((largeur || 320) - 228, (menu?.vi || 0) * colW + colW / 2 - 110)) },
          ]}>
            {items.map((it, i) => {
              if (it.separateur) return <View key={i} style={s.menuSep} />;
              if (it.entete) return <Text key={i} style={s.menuEntete}>{it.entete}</Text>;
              return (
              // `disabled` : l'entrée reste VISIBLE mais grisée et inerte — on
              // montre ce qui existe sans y donner accès (fonction hors forfait).
              <TouchableOpacity key={i} disabled={!!it.disabled}
                style={[s.menuItem, i > 0 && !items[i - 1]?.entete && s.menuItemBord, it.disabled && s.menuItemOff]}
                onPress={() => {
                  const f = it.onPress; const cible = it.navigateVers || menu?.cle;
                  setMenu(null);
                  if (cible) navigation.navigate(cible);
                  f && f();
                }}>
                {!!it.icone && <Text style={[s.menuIc, it.disabled && s.menuOff]}>{it.icone}</Text>}
                {/* flexShrink + 2 lignes max : un libellé long (selon la langue)
                    passe à la ligne au lieu de déborder du menu. */}
                <Text style={[s.menuTxt, it.disabled && s.menuOff]} numberOfLines={2}>{it.label}</Text>
                {it.disabled && <Text style={s.menuCadenas}>🔒</Text>}
              </TouchableOpacity>
              );
            })}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

export default function CoquilleApp({ onglets = [], badges = {}, avecPub = true, ecranConnexion }) {
  const UI = useUI();
  const t = useT();
  const { user, loading } = useAuth();
  const { pret: espacePret } = useEspace();

  // Chargement de l'authentification.
  if (loading) return <EcranChargement UI={UI} />;

  if (!user) {
    return (
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        {ecranConnexion || <EcranConnexion />}
        <VersionFooter />
      </View>
    );
  }

  // Connecté mais données de base pas encore prêtes (espaces) : on BLOQUE l'app
  // derrière un spinner — l'utilisateur ne peut pas agir sur des données absentes.
  if (!espacePret) return <EcranChargement UI={UI} message={t('commun.chargementEspaces')} />;

  return (
    <MenuOngletProvider>
      <NavigationContainer>
        <Tab.Navigator
          screenOptions={{ headerShown: false }}
          tabBar={(props) => <BarreOnglets {...props} onglets={onglets} badges={badges} avecPub={avecPub} />}
        >
          {onglets.map(o => (
            <Tab.Screen key={o.cle} name={o.cle} component={o.composant} />
          ))}
        </Tab.Navigator>
      </NavigationContainer>
    </MenuOngletProvider>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  bar:   { flexDirection: 'row', backgroundColor: UI.card, borderTopWidth: 1, borderTopColor: UI.border, paddingTop: 6 },
  item:  { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 2 },
  icon:  { fontSize: 20 },
  label: { fontSize: 11, fontWeight: '500' },
  dot:   { position: 'absolute', top: -5, right: -10, backgroundColor: '#EF4444', borderRadius: 9,
           minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center',
           paddingHorizontal: 3, borderWidth: 1.5, borderColor: '#fff' },
  dotTxt:{ color: '#fff', fontSize: 9, fontWeight: '800' },
  chevMenu: { position: 'absolute', bottom: -3, right: -9, fontSize: 10, color: UI.accent, fontWeight: '900' },
  menuFond: { flex: 1, backgroundColor: 'transparent' },
  menu:  { position: 'absolute', width: 220, backgroundColor: UI.card, borderRadius: 12,
           borderWidth: 1, borderColor: UI.border, paddingVertical: 4,
           shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 14, shadowOffset: { width: 0, height: 6 }, elevation: 12 },
  menuItem: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 14 },
  menuItemBord: { borderTopWidth: 0.5, borderTopColor: UI.border },
  menuItemOff: { opacity: 0.5 },
  menuOff: { color: UI.faint },
  menuCadenas: { fontSize: 12, marginLeft: 'auto' },
  menuSep: { height: 2.5, backgroundColor: UI.border },
  menuEntete: { fontSize: 9.5, color: UI.faint, fontWeight: '800', letterSpacing: 0.6, paddingHorizontal: 14, paddingTop: 7, paddingBottom: 2 },
  menuIc:  { fontSize: 17 },
  menuTxt: { fontSize: 14.5, fontWeight: '600', color: UI.ink, flex: 1, flexShrink: 1 },
});
